import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { DynamicEntityRow } from '../../../api/strapiDynamicEntity';
import { bidangLabelFromSamplingPlan, type StrapiSampleInformationRow, type StrapiSurveyGridRow } from '../../../api/strapiCrm';
import {
  letterCountBySamplingPlanFromRows,
  pickCanonicalSamplingPlans,
  samplingPlanCompletenessScore,
} from '../../../sampling/samplingPlanCanonical';

function relationMany(plan: DynamicEntityRow, field: string): DynamicEntityRow[] {
  const raw = plan[field];
  if (!Array.isArray(raw)) return [];
  return raw.filter((v) => v && typeof v === 'object') as DynamicEntityRow[];
}

function referensiLabels(items: DynamicEntityRow[]): string[] {
  return items
    .map((item) => {
      const parts = [
        String(item.kode_kategori ?? '').trim(),
        String(item.kode ?? '').trim(),
        String(item.deskripsi1 ?? '').trim(),
      ].filter(Boolean);
      return parts.join(' — ') || String(item.documentId ?? '').trim();
    })
    .filter(Boolean);
}

type Props = {
  plans: DynamicEntityRow[];
  surveyRows: StrapiSurveyGridRow[];
  sampleLines?: StrapiSampleInformationRow[];
};

export function SurveyWorkOrderPlansReadonly({ plans, surveyRows, sampleLines = [] }: Props) {
  const canonicalPlans = useMemo(() => {
    const letterCounts = letterCountBySamplingPlanFromRows(surveyRows);
    return pickCanonicalSamplingPlans(plans, letterCounts);
  }, [plans, surveyRows]);

  if (!canonicalPlans.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Belum ada rencana sampling work order yang dapat ditampilkan.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Rencana sampling work order
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Referensi admin — data terbaru saat halaman dimuat
        </Typography>
      </Stack>
      {canonicalPlans.map((plan, idx) => {
        const planId = String(plan.documentId ?? '').trim() || `plan-${idx}`;
        const bidang = bidangLabelFromSamplingPlan(plan, plans, sampleLines) || '—';
        const pointCount = Math.max(1, Math.floor(Number(plan.sample_point_count) || 1));
        const peralatan = referensiLabels(relationMany(plan, 'peralatan_pengambilan_contoh')).slice(0, 6);
        const metode = relationMany(plan, 'metode_sampling')
          .map((m) => String(m.kode_sni ?? m.documentId ?? '').trim())
          .filter(Boolean)
          .slice(0, 4);
        const sniMethods = relationMany(plan, 'sni_methods')
          .map((m) => {
            const kode = String(m.kode_sni ?? '').trim();
            const judul = String(m.judul ?? '').trim();
            return [kode, judul].filter(Boolean).join(' — ') || String(m.documentId ?? '').trim();
          })
          .filter(Boolean)
          .slice(0, 4);
        const pengawetan = referensiLabels(relationMany(plan, 'pengawetan')).slice(0, 4);

        return (
          <Accordion key={planId} disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }} flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {bidang}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {pointCount} titik · {String(plan.progress_status ?? 'draft')} · checklist {samplingPlanCompletenessScore(plan)}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {peralatan.length ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>
                      Peralatan:
                    </Typography>
                    {peralatan.map((label) => (
                      <Chip key={`${planId}-alat-${label}`} size="small" label={label} />
                    ))}
                  </Stack>
                ) : null}
                {metode.length ? (
                  <Typography variant="caption" color="text.secondary">
                    Parameter: {metode.join(', ')}
                  </Typography>
                ) : null}
                {sniMethods.length ? (
                  <Typography variant="caption" color="text.secondary">
                    Metode sampling: {sniMethods.join(', ')}
                  </Typography>
                ) : null}
                {pengawetan.length ? (
                  <Typography variant="caption" color="text.secondary">
                    Pengawetan: {pengawetan.join(', ')}
                  </Typography>
                ) : null}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}
